import type { ChatContactWithChat } from '../types'
import type { IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'

function parseTimestamp(value?: string | null): number | null {
  if (!value?.trim()) {
    return null
  }

  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

function getContactLastMessageTime(contact: ChatContactWithChat): number | null {
  return parseTimestamp(contact.chat?.lastMessage?.time)
}

function getContactCreatedAt(contact: ChatContactWithChat): number {
  const chat = contact.chat as { created_at?: string } | undefined
  return parseTimestamp(chat?.created_at) ?? 0
}

function compareContactsByStableId(a: ChatContactWithChat, b: ChatContactWithChat): number {
  const aId = String(a.chat?.id ?? a.id)
  const bId = String(b.chat?.id ?? b.id)
  if (aId === bId) {
    return 0
  }

  const aNumeric = Number(aId)
  const bNumeric = Number(bId)
  const bothNumeric = Number.isFinite(aNumeric) && Number.isFinite(bNumeric)

  if (bothNumeric) {
    return bNumeric - aNumeric
  }

  return bId.localeCompare(aId)
}

/**
 * Время для сортировки: последнее сообщение или дата создания.
 * Для пространства / topics-группы учитывает активность дочерних элементов.
 */
export function getContactActivityTimestamp(
  contact: ChatContactWithChat,
  children?: ChatContactWithChat[],
): number {
  if (children?.length) {
    let maxChildTime = 0
    for (const child of children) {
      maxChildTime = Math.max(maxChildTime, getContactActivityTimestamp(child))
    }
    if (maxChildTime > 0) {
      return maxChildTime
    }
  }

  return getContactLastMessageTime(contact) ?? getContactCreatedAt(contact)
}
export interface SidebarSpaceItem {
  kind: 'space'
  contact: ChatContactWithChat
  children: ChatContactWithChat[]
  unread: number
}

export interface SidebarTopicsGroupItem {
  kind: 'topics-group'
  contact: ChatContactWithChat
  topics: IChatTopic[]
  unread: number
}

export interface SidebarChatItem {
  kind: 'chat'
  contact: ChatContactWithChat
}

export type SidebarListItem = SidebarSpaceItem | SidebarTopicsGroupItem | SidebarChatItem

export function sortSidebarListItems(items: SidebarListItem[]): SidebarListItem[] {
  return [...items].sort((a, b) => {
    const aPinned = Boolean(a.contact.chat?.is_pinned)
    const bPinned = Boolean(b.contact.chat?.is_pinned)
    if (aPinned !== bPinned) {
      return aPinned ? -1 : 1
    }

    if (aPinned && bPinned) {
      const pinnedDiff = (parseTimestamp(b.contact.chat?.pinned_at) ?? 0)
        - (parseTimestamp(a.contact.chat?.pinned_at) ?? 0)
      if (pinnedDiff !== 0) {
        return pinnedDiff
      }
    }

    const timeA = a.kind === 'space'
      ? getContactActivityTimestamp(a.contact, a.children)
      : getContactActivityTimestamp(a.contact)
    const timeB = b.kind === 'space'
      ? getContactActivityTimestamp(b.contact, b.children)
      : getContactActivityTimestamp(b.contact)

    const timeDiff = timeB - timeA
    if (timeDiff !== 0) {
      return timeDiff
    }

    return compareContactsByStableId(a.contact, b.contact)
  })
}
